import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * RouteReuseStrategy needs to be provided in app.module
 */
@Injectable({
    providedIn: 'root',
})
export class RouteReuseService implements RouteReuseStrategy {
    private storedRouteHandles: Map<string, DetachedRouteHandle> = new Map<string, DetachedRouteHandle>();
    private currentPath: string | undefined;
    private futurePath: string | undefined;

    // ActivatedRouteSnapshot path for list pages might be '' for lazy loaded feature modules
    // therefore add path from parent route to have a unique Map key
    private pathPrefix: string | undefined;

    /**
     * This method is called every time we navigate between routes.
     * The future is the route we are landing to.
     * The current is route we are leaving.
     * If it returns TRUE the routing will not happen (which means that routing has not changed).
     * If it returns FALSE then the routing happens and the rest of the methods are called.
     */
    shouldReuseRoute(future: ActivatedRouteSnapshot, current: ActivatedRouteSnapshot): boolean {
        if (future.routeConfig && current.routeConfig) {
            // Here we keep the currentPath as a path we are leaving and futurePath as a path we
            // are landing to, because we want to use cached component we have just left in case we
            // are coming back to it. This will happen in retrieve method which is called only if
            // shouldAttach method returns true.
            this.currentPath = current.routeConfig.path;
            this.futurePath = future.routeConfig.path;
            this.pathPrefix = current.parent?.routeConfig?.path || '';
        }
        return future.routeConfig === current.routeConfig;
    }

    /**
     * It is invoked when we leave the current route.
     * If returns TRUE then the store method will be invoked.
     */
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        if (!route.routeConfig || route.routeConfig.loadChildren) {
            return false;
        }
        let shouldReuse = false;

        if (route?.data?.routeReuseComingFromPagePath && route?.data?.routeReuseComingFromPagePath === this.futurePath) {
            shouldReuse = true;
        }

        return shouldReuse;
    }

    /**
     * This method is invoked only if the shouldDetach returns true.
     * We can manage here how to store the RouteHandle.
     * What we store here will be used in the retrieve method.
     * It provides the route we are leaving and the RouteHandle.
     */
    store(route: ActivatedRouteSnapshot, handler: DetachedRouteHandle): void {
        const path: string | undefined = this.getPath(route);
        if (handler && path !== undefined) {
            this.storedRouteHandles.set(path, handler);
        }
    }

    /**
     * This method is called for the route just opened when we land on the component of this route.
     * Once component is loaded this method is called.
     * If this method returns TRUE then retrieve method will be called,
     * otherwise the component will be created from scratch.
     */
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        const path: string | undefined = this.getPath(route);
        if (path === undefined) {
            return false;
        }

        if (this.currentPath === route?.data?.routeReuseComingFromPagePath) {
            return this.storedRouteHandles.has(path);
        }
        return false;
    }

    /**
     * This method is called if shouldAttach returns TRUE, provides as parameter
     * the current route (we just land), and returns a stored RouteHandle.
     * If returns null has no effects.
     * We can use this method to get any stored RouteHandle manually.
     * This is not managed by the framework automatically. It is our responsibility to implement it.
     */
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        const path: string | undefined = this.getPath(route);
        if (!route.routeConfig || route.routeConfig.loadChildren || !path) {
            return null;
        }
        const handler: DetachedRouteHandle | undefined = this.storedRouteHandles.get(path);
        return handler ?? null;
    }

    /**
     * add pathPrefix from parent route to have a unique Map key
     */
    private getPath(route: ActivatedRouteSnapshot): string | undefined {
        if (route.routeConfig) {
            return (this.pathPrefix || '') + (this.pathPrefix && route.routeConfig.path ? '/' : '') + route.routeConfig.path;
        }
        return undefined;
    }
}
